// src/security/SecureQuantumPage.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { QuantumErrorBoundary } from '@/components/QuantumErrorBoundary';
import { QuantumSuspense, QuantumPageLoader } from '@/components/QuantumLoadingStates';

// ─── PRODUCTION: tier gating disabled — flip to true when ready to monetise ──
const ENABLE_TIER_GATING = false;

interface SecureQuantumPageProps {
  children: React.ReactNode;
  // Accepts both vocabularies — see the comment on tierOrder below.
  requireTier?: 'free' | 'basic' | 'journey' | 'premium' | 'journey_pro' | 'premium_pro' | 'agency' | 'admin';
  multiverseAccess?: boolean;
  skipAuth?: boolean;
}

export function SecureQuantumPage({
  children,
  requireTier,
  multiverseAccess = false,
  skipAuth = false,
}: SecureQuantumPageProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // 1. Fully public route — no auth check at all
  if (skipAuth) {
    return (
      <QuantumErrorBoundary>
        <QuantumSuspense fallback={<QuantumPageLoader message="Loading..." />}>
          {children}
        </QuantumSuspense>
      </QuantumErrorBoundary>
    );
  }

  // 2. Still loading auth state — show spinner
  if (isLoading) {
    return <QuantumPageLoader message="Authenticating..." />;
  }

  // 3. Not logged in — send to login, preserve intended destination
  if (!user) {
    if (
      !location.pathname.startsWith('/login') &&
      !location.pathname.startsWith('/signup')
    ) {
      localStorage.setItem('returnTo', location.pathname);
    }
    return <Navigate to="/login" replace />;
  }

  // 4. Auto-mark onboarding complete so users never get stuck in a loop
  if (localStorage.getItem('onboarding_completed') !== 'true') {
    localStorage.setItem('onboarding_completed', 'true');
  }

  // 5. Tier gate — only runs when ENABLE_TIER_GATING = true
  //    In production (false) every authenticated user passes through freely.
  if (ENABLE_TIER_GATING && requireTier) {
    const devOverride = localStorage.getItem('DEV_TIER_OVERRIDE') === 'true';
    const userTier = user?.tier || 'free';   // fail CLOSED — never trust localStorage, never default to paid

    if (!devOverride) {
      // 2026-07-23 renamed users.tier's vocabulary from basic/premium/premium_pro
      // to free/journey/journey_pro (confirmed straight rename, not a re-tiering
      // — docs/PROJECT_HISTORY.md). Both vocabularies map to the same levels:
      // real DB values (journey/journey_pro) arrive via userTier, route
      // declarations (still 100% pre-rename as of 2026-09-04) arrive via
      // requireTier — either side may use either vocabulary.
      const tierOrder: Record<string, number> = {
        free: 0, basic: 0,
        journey: 1, premium: 1,
        journey_pro: 2, premium_pro: 2,
        agency: 3, admin: 4,
      };
      // Fail safe: an unmapped tier (typo, drift, a future vocabulary change)
      // must never be treated as if it were mid-tier access. Unknown userTier
      // gets the lowest level (denied by default); unknown requireTier gets
      // the highest level (blocks by default rather than silently ungating
      // a misconfigured route).
      const userLevel     = tierOrder[userTier]    ?? 0;
      const requiredLevel = tierOrder[requireTier] ?? 4;

      if (userLevel < requiredLevel) {
        return (
          <Navigate
            to={`/upgrade?returnTo=${encodeURIComponent(location.pathname)}`}
            replace
          />
        );
      }

      if (multiverseAccess && userTier !== 'premium_pro' && userTier !== 'journey_pro' && userTier !== 'admin') {
        return <Navigate to="/vgc?feature=multiverse" replace />;
      }
    }
  }

  // 6. All checks passed — render the page
  return (
    <QuantumErrorBoundary>
      <QuantumSuspense fallback={<QuantumPageLoader message="Loading secure page..." />}>
        {children}
      </QuantumSuspense>
    </QuantumErrorBoundary>
  );
}

export default SecureQuantumPage;